/* Peachy — predictive search
   Renders the `predictive-search` section via the Section Rendering API.
   In-flight requests are aborted so a slow early response can never overwrite
   the results for what the shopper has actually typed. */

(function () {
  'use strict';

  const P = window.Peachy || {};
  const define = P.define || ((n, c) => (customElements.get(n) ? null : customElements.define(n, c)));

  define(
    'predictive-search',
    class PredictiveSearch extends HTMLElement {
      #controller = null;
      #cache = new Map();

      connectedCallback() {
        this.input = this.querySelector('input[type="search"]');
        this.results = this.querySelector('[data-predictive-results]');
        this.status = this.querySelector('[data-predictive-status]');
        this.defaultPanel = this.querySelector('[data-predictive-default]');
        this.resetButton = this.querySelector('[data-search-reset]');
        if (!this.input || !this.results) return;

        this.input.setAttribute('role', 'combobox');
        this.input.setAttribute('aria-expanded', 'false');
        this.input.setAttribute('aria-autocomplete', 'list');
        this.input.setAttribute('autocomplete', 'off');

        this.input.addEventListener('input', P.debounce(() => this.#onInput(), 260));
        this.input.addEventListener('keydown', (event) => this.#onKeydown(event));

        if (this.resetButton) {
          this.resetButton.addEventListener('click', () => {
            this.input.value = '';
            this.#clear();
            this.input.focus();
          });
        }
      }

      disconnectedCallback() {
        this.#controller?.abort();
      }

      #onInput() {
        const term = this.input.value.trim();
        if (this.resetButton) this.resetButton.hidden = term.length === 0;
        if (term.length < 2) {
          this.#clear();
          return;
        }
        this.#search(term);
      }

      #clear() {
        this.#controller?.abort();
        this.results.innerHTML = '';
        this.results.hidden = true;
        if (this.defaultPanel) this.defaultPanel.hidden = false;
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');
        if (this.status) this.status.textContent = '';
      }

      async #search(term) {
        if (this.#cache.has(term)) {
          this.#render(this.#cache.get(term));
          return;
        }

        this.#controller?.abort();
        this.#controller = new AbortController();
        if (this.status) this.status.textContent = P.strings.searchLoading || '';

        const url = new URL(P.routes.predictive_search, window.location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('resources[type]', 'product,collection,page,article');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('section_id', 'predictive-search');

        try {
          const response = await fetch(url, { signal: this.#controller.signal });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const text = await response.text();
          const parsed = new DOMParser().parseFromString(text, 'text/html');
          const payload = parsed.querySelector('[data-predictive-payload]');
          const html = payload ? payload.innerHTML : '';
          this.#cache.set(term, html);
          this.#render(html);
        } catch (error) {
          if (error.name === 'AbortError') return;
          if (this.status) this.status.textContent = '';
        }
      }

      #render(html) {
        this.results.innerHTML = html;
        this.results.hidden = false;
        if (this.defaultPanel) this.defaultPanel.hidden = true;
        this.input.setAttribute('aria-expanded', 'true');

        const count = this.#options.length;
        if (!this.status) return;
        const template = count === 1 ? P.strings.resultsOne : P.strings.resultsOther;
        this.status.textContent = (template || '').replace('[count]', count);
      }

      get #options() {
        return Array.from(this.results.querySelectorAll('[data-predictive-option]'));
      }

      #onKeydown(event) {
        if (event.key === 'Escape') {
          this.#clear();
          return;
        }

        const options = this.#options;
        if (!options.length) return;

        const current = options.findIndex((el) => el.getAttribute('aria-selected') === 'true');

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const next =
            event.key === 'ArrowDown'
              ? Math.min(current + 1, options.length - 1)
              : Math.max(current - 1, 0);

          for (const option of options) option.setAttribute('aria-selected', 'false');
          const target = options[next];
          target.setAttribute('aria-selected', 'true');
          target.scrollIntoView({ block: 'nearest' });
          if (target.id) this.input.setAttribute('aria-activedescendant', target.id);
          return;
        }

        if (event.key === 'Enter' && current > -1) {
          event.preventDefault();
          options[current].click();
        }
      }
    }
  );
})();
